import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { SubscriptionPlan } from '../models/platform-access.models';
import { User } from './auth.service';

export interface BillingApiError {
  code: number;
  message: string;
  details?: string[];
}

export interface BillingSummary {
  environment: 'sandbox' | 'live';
  paddleAvailable: boolean;
  clientTokenAvailable: boolean;
  portalAvailable: boolean;
  customerId: string | null;
  subscriptionId: string | null;
  subscriptionStatus: string | null;
  nextBilledAt: string | null;
  scheduledChangeAction: string | null;
  scheduledChangeEffectiveAt: string | null;
}

export type BillingCheckoutMode =
  | 'noop'
  | 'inline_checkout'
  | 'upgrade_immediate'
  | 'downgrade_next_cycle'
  | 'cancel_next_cycle'
  | 'subscription_missing';

export interface PreparedCheckout {
  environment: 'sandbox' | 'live';
  clientToken: string;
  priceId: string;
  customerId: string;
  customerAuthToken: string | null;
  customData: Record<string, unknown>;
}

interface BillingPlansResponse {
  success: boolean;
  currentUser: User;
  plans: SubscriptionPlan[];
  billing: BillingSummary;
}

interface BillingPrepareCheckoutResponse {
  success: boolean;
  mode: BillingCheckoutMode;
  requiresConsent: boolean;
  summaryMessage: string;
  consentVersion: string;
  consentText: string;
  currentUser: User;
  plan: SubscriptionPlan;
  billing: BillingSummary;
  checkout: PreparedCheckout | null;
}

interface BillingChangePlanResponse {
  success: boolean;
  message: string;
  user: User;
  billing: BillingSummary;
}

interface BillingPortalSessionResponse {
  success: boolean;
  url: string;
}

interface BillingCompleteCheckoutResponse {
  success: boolean;
  message: string;
  user: User;
  billing: BillingSummary;
}

export interface BillingPlansState {
  currentUser: User;
  plans: SubscriptionPlan[];
  billing: BillingSummary;
}

export interface BillingCheckoutState {
  mode: BillingCheckoutMode;
  requiresConsent: boolean;
  summaryMessage: string;
  consentVersion: string;
  consentText: string;
  currentUser: User;
  plan: SubscriptionPlan;
  billing: BillingSummary;
  checkout: PreparedCheckout | null;
}

@Injectable({
  providedIn: 'root'
})
export class BillingService {
  private readonly API_URL = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  getPlans(): Observable<BillingPlansState> {
    return this.http
      .get<BillingPlansResponse>(`${this.API_URL}/billing/plans`)
      .pipe(
        map((response) => ({
          currentUser: response.currentUser,
          plans: response.plans,
          billing: response.billing
        })),
        catchError(this.handleError)
      );
  }

  prepareCheckout(planCode: string, consentAccepted = false): Observable<BillingCheckoutState> {
    return this.http
      .post<BillingPrepareCheckoutResponse>(`${this.API_URL}/billing/checkout/prepare`, {
        planCode,
        consentAccepted
      })
      .pipe(
        map((response) => ({
          mode: response.mode,
          requiresConsent: response.requiresConsent,
          summaryMessage: response.summaryMessage,
          consentVersion: response.consentVersion,
          consentText: response.consentText,
          currentUser: response.currentUser,
          plan: response.plan,
          billing: response.billing,
          checkout: response.checkout
        })),
        catchError(this.handleError)
      );
  }

  changePlan(planCode: string): Observable<{ message: string; user: User; billing: BillingSummary }> {
    return this.http
      .post<BillingChangePlanResponse>(`${this.API_URL}/billing/change-plan`, { planCode })
      .pipe(
        map((response) => ({
          message: response.message,
          user: response.user,
          billing: response.billing
        })),
        catchError(this.handleError)
      );
  }

  completeCheckout(payload: {
    planCode: string;
    customerId: string;
    transactionId?: string | null;
    priceId?: string | null;
  }): Observable<{ message: string; user: User; billing: BillingSummary }> {
    return this.http
      .post<BillingCompleteCheckoutResponse>(`${this.API_URL}/billing/checkout/complete`, payload)
      .pipe(
        map((response) => ({
          message: response.message,
          user: response.user,
          billing: response.billing
        })),
        catchError(this.handleError)
      );
  }

  createPortalSession(): Observable<string> {
    return this.http
      .post<BillingPortalSessionResponse>(`${this.API_URL}/billing/portal-session`, {})
      .pipe(map((response) => response.url), catchError(this.handleError));
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let message = 'An unexpected error occurred';

    if (error.error?.error?.message) {
      message = error.error.error.message as string;
    } else if (error.error?.message) {
      message = error.error.message as string;
    } else if (error.message) {
      message = error.message;
    }

    return throwError(() => ({
      code: error.status,
      message,
      details: error.error?.error?.details as string[] | undefined,
    } as BillingApiError));
  };
}
